import { ReportContext } from "../types/reporting.types";

export interface ReportingPermissions {
	canEditProject: boolean;
	canEditReport: boolean;
	canSubmit: boolean;
	canApprove: boolean;
	canClose: boolean;
	canDelete: boolean;
	canRequestChanges: boolean;
	canReject: boolean;
	canUpdateStatus: boolean;
	canAdminSave: boolean;
	canManageAdditionalAuthors: boolean;
}

export const IMMUTABLE_FIELDS = ["report_number", "created_at", "updated_at"];

export function getReportingPermissions(
	ctx: ReportContext
): ReportingPermissions {
	const isAdmin = ctx.isSuperAdmin || ctx.isTenantAdmin;

	return {
		canEditProject:
			isAdmin ||
			ctx.mode === "create" ||
			(ctx.isAuthor && ctx.status === "draft"),
		canEditReport:
			isAdmin ||
			ctx.mode === "create" ||
			(ctx.isAuthor &&
				(ctx.status === "draft" || ctx.openStatus === "changes_required")) ||
			(ctx.isReviewer && ctx.openStatus === "in_review"),
		canSubmit:
			isAdmin ||
			(ctx.isAuthor &&
				(ctx.mode === "create" ||
					ctx.status === "draft" ||
					ctx.openStatus === "changes_required")),
		canApprove:
			isAdmin ||
			(ctx.isReviewer &&
				ctx.status === "open" &&
				["in_review", "final_review"].includes(ctx.openStatus ?? "")),
		canClose:
			isAdmin ||
			(ctx.isReviewer &&
				ctx.status === "open" &&
				ctx.openStatus === "ready_to_close"),
		canDelete:
			isAdmin ||
			(ctx.isAuthor && ctx.status === "draft") ||
			(ctx.isReviewer &&
				ctx.status === "open" &&
				["in_review", "final_review"].includes(ctx.openStatus ?? "")),
		canRequestChanges:
			isAdmin ||
			(ctx.isReviewer &&
				ctx.status === "open" &&
				["in_review", "final_review"].includes(ctx.openStatus ?? "")),
		canReject:
			isAdmin ||
			(ctx.isReviewer &&
				ctx.status === "open" &&
				ctx.openStatus === "in_review"),
		canUpdateStatus: ctx.isSuperAdmin || ctx.isTenantAdmin,
		canAdminSave: isAdmin && ctx.mode === "edit" && ctx.status !== "draft",
		canManageAdditionalAuthors:
			(ctx.mode === "create" || ctx.status === "draft") && ctx.isPrimaryAuthor,
	};
}

export function canManageFollowUpActions(ctx: ReportContext): boolean {
	if (ctx.isReadOnly) return false;
	const permissions = getReportingPermissions(ctx);
	const isReviewerDuringReview =
		ctx.isReviewer &&
		ctx.status === "open" &&
		["in_review", "final_review"].includes(ctx.openStatus ?? "");
	if (
		!permissions.canEditReport &&
		ctx.mode === "edit" &&
		!isReviewerDuringReview
	)
		return false;
	return ctx.status !== "closed" && ctx.openStatus !== "in_progress";
}

export function isFieldReadOnly(
	fieldId: string,
	permissions: ReportingPermissions,
	mode: "create" | "edit"
): boolean {
	// Immutable fields are always read-only
	if (IMMUTABLE_FIELDS.includes(fieldId)) return true;

	// Operator is read-only in edit mode (tenant isolation)
	if (fieldId === "operator" && mode === "edit") return true;

	return false;
}
